import { RaterXPreset } from '../../api/types/rater-x-preset';
import { CoverageState, RatingOptions } from '../../types';
import { UnderwriteQuoteState } from '../../views/underwriter/types';
import { flattenCoverages } from '../coverages';
import { flattenModifiers } from '../uw/ratingOptions';

export const mergeCoverageReferralReasonsForEdit = (
  coverageCategories: RaterXPreset['coverageCategories'],
  coverageState: CoverageState,
  productTypePrefix?: string
) => {
  const flattenedCoverages = flattenCoverages(coverageCategories);
  const updatedState = { ...coverageState };

  flattenedCoverages.forEach((coverage) => {
    const coverageKey = productTypePrefix
      ? `${coverage.key}.${productTypePrefix}`
      : coverage.key;
    const _coverageState = updatedState[coverageKey];
    if (_coverageState && coverage.referralReasonsForEdit) {
      const existingReasons = _coverageState.referralReasonsForEdit || [];
      const newReasons = coverage.referralReasonsForEdit.filter(
        (reason) => !existingReasons.includes(reason)
      );
      updatedState[coverageKey] = {
        ..._coverageState,
        referralReasonsForEdit: [...existingReasons, ...newReasons],
      };
    }
  });
  return updatedState;
};

export const mergeRateModifierReferralReasonsForEdit = (
  rateModifierCategories: RaterXPreset['rateModifierCategories'],
  ratingOptions: RatingOptions,
  productTypePrefix?: string
) => {
  const flattenedModifiers = flattenModifiers(rateModifierCategories)?.filter(Boolean);
  const updatedOptions = { ...ratingOptions };

  flattenedModifiers?.forEach((modifier) => {
    const modifierKey = productTypePrefix
      ? `${modifier.key}.${productTypePrefix}`
      : modifier.key;
    const _ratingOptionState = updatedOptions[modifierKey];
    if (_ratingOptionState && modifier.referralReasonsForEdit) {
      const existingReasons = _ratingOptionState.referralReasonsForEdit || [];
      const newReasons = modifier.referralReasonsForEdit.filter(
        (reason) => !existingReasons.includes(reason)
      );
      updatedOptions[modifierKey] = {
        ..._ratingOptionState,
        referralReasonsForEdit: [...existingReasons, ...newReasons],
      };
    }
  });
  return updatedOptions;
};

export const mergeComponentReferralReasonsForEdit = (
  components: RaterXPreset['components'],
  coverageState: CoverageState,
  ratingOptions: RatingOptions
) => {
  if (!components) {
    return { coverageState, ratingOptions };
  }

  Object.entries(components).forEach(([productType, component]) => {
    coverageState = mergeCoverageReferralReasonsForEdit(
      component.coverageCategories,
      coverageState,
      productType
    );
    ratingOptions = mergeRateModifierReferralReasonsForEdit(
      component.rateModifierCategories,
      ratingOptions,
      productType
    );
  });

  return { coverageState, ratingOptions };
};

export const mergeReferralReasonsForEditIntoQuoteState = <
  T extends Partial<UnderwriteQuoteState>
>(
  state: T
) => {
  const raterResponse =
    state?.quote?.quote?.raterResponse || state?.raterX || null;
  let coverageState = mergeCoverageReferralReasonsForEdit(
    raterResponse?.coverageCategories || [],
    state.coverageState || {}
  );
  let ratingOptions = mergeRateModifierReferralReasonsForEdit(
    raterResponse?.rateModifierCategories || [],
    state.ratingOptions || {}
  );

  // Handle component-based structures (ZSP products)
  if (raterResponse?.components) {
    const componentMerged = mergeComponentReferralReasonsForEdit(
      raterResponse.components,
      coverageState,
      ratingOptions
    );
    coverageState = componentMerged.coverageState;
    ratingOptions = componentMerged.ratingOptions;
  }

  return {
    ...state,
    coverageState,
    ratingOptions,
  };
};
